import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { DownloadSimple, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Checklists() {
  const [items, setItems] = useState([]);
  const [checked, setChecked] = useState({}); // { listId: Set(index) }

  useEffect(() => { api.get("/checklists").then((r) => setItems(r.data)); }, []);

  const toggle = (id, idx) => {
    setChecked((c) => {
      const s = new Set(c[id] || []);
      s.has(idx) ? s.delete(idx) : s.add(idx);
      return { ...c, [id]: s };
    });
  };

  const download = (list) => {
    const text = [`# ${list.title}`, "", list.description, "", ...list.items.map((x, i) => `- [ ] ${x}`)].join("\n");
    const blob = new Blob([text], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${list.id}.md`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Downloaded");
  };

  return (
    <div className="space-y-10">
      <header className="fade-up">
        <div className="overline mb-3">Playbooks & Checklists</div>
        <h1 className="font-display text-5xl lg:text-6xl tracking-tighter leading-[0.95]">Copy-ready. Day-one usable.</h1>
        <p className="text-muted-foreground mt-4 max-w-2xl">Print, share, or download the playbooks the pros keep pinned to their whiteboard.</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border-t border-l border-border">
        {items.map((list) => {
          const cset = checked[list.id] || new Set();
          const pct = Math.round((cset.size / list.items.length) * 100);
          return (
            <div key={list.id} className="p-6 lg:p-8 bg-white border-r border-b border-border" data-testid={`checklist-${list.id}`}>
              <div className="flex items-start justify-between gap-4 mb-2">
                <div>
                  <div className="font-display text-2xl leading-tight">{list.title}</div>
                  <p className="text-sm text-muted-foreground mt-1">{list.description}</p>
                </div>
                <button onClick={() => download(list)} data-testid={`checklist-download-${list.id}`}
                  className="text-xs flex items-center gap-1.5 border border-border px-3 py-2 hover:bg-secondary transition-colors">
                  <DownloadSimple size={14} /> .md
                </button>
              </div>
              <div className="w-full bg-secondary h-1 mt-4">
                <div className="h-1 bg-primary transition-all" style={{ width: `${pct}%` }} />
              </div>
              <ul className="mt-5 space-y-2">
                {list.items.map((it, idx) => {
                  const on = cset.has(idx);
                  return (
                    <li key={idx}>
                      <button onClick={() => toggle(list.id, idx)}
                        data-testid={`checklist-item-${list.id}-${idx}`}
                        className="w-full text-left flex items-start gap-3 py-1 group">
                        <span className={`shrink-0 w-5 h-5 border ${on ? "bg-primary border-primary" : "border-border group-hover:border-foreground"} flex items-center justify-center mt-0.5`}>
                          {on && <Check size={12} weight="bold" className="text-primary-foreground" />}
                        </span>
                        <span className={`text-sm ${on ? "line-through text-muted-foreground" : "text-foreground"}`}>{it}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}
